import { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { CHATBOT_ID } from '../constants/chatbotId';
import { currentOpenChatSelector } from '../selectors';

export const useLongPress = (
  onLongPress: (event: Event) => void,
  onClick: () => void,
  { shouldPreventDefault = true, delay = 750 } = {},
) => {
  const [longPressTriggered, setLongPressTriggered] = useState(false);
  const timeout = useRef<number | null>(null);
  const currentChat = useSelector(currentOpenChatSelector);

  const effectiveDelay = currentChat?.chatId === CHATBOT_ID ? delay * 2 : delay;

  const start = useCallback(
    (event: Event) => {
      timeout.current = window.setTimeout(() => {
        onLongPress(event);
        setLongPressTriggered(true);
      }, effectiveDelay);
    },
    [onLongPress, effectiveDelay],
  );

  const clear = useCallback(
    (event: Event, shouldTriggerClick = true) => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
      if (shouldTriggerClick && !longPressTriggered) {
        onClick();
      }

      if (shouldPreventDefault && event.target && longPressTriggered) {
        event.preventDefault();
      }

      setLongPressTriggered(false);
    },
    [shouldPreventDefault, onClick, longPressTriggered],
  );

  return {
    onMouseDown: (e: MouseEvent) => start(e),
    onTouchStart: (e: TouchEvent) => start(e),
    onMouseUp: (e: MouseEvent) => clear(e),
    onMouseLeave: (e: MouseEvent) => clear(e, false),
    onTouchEnd: (e: TouchEvent) => clear(e),
  };
};
